/* eslint-disable @typescript-eslint/no-floating-promises */

import {clickOnProductOptionSf, clickShippingInfoLinkSf} from '@wix/bi-logger-ec-sf/v2';
import {IControllerConfig} from '@wix/native-components-infra/dist/es/src/types/types';
import {getStylesValues} from '@wix/tpa-settings';
import {createSlotVeloAPIFactory} from '@wix/widget-plugins-ooi/velo';
import {AddToCartActionOption, StoresWidgetID} from '@wix/wixstores-client-core/dist/es/src/constants';
import {generateGuid} from '@wix/wixstores-client-core/dist/es/src/guid-generator';
import {actualPrice, isPreOrder} from '@wix/wixstores-client-core/dist/es/src/productOptions/productUtils';
import {HeadingTags} from '@wix/wixstores-client-core/dist/es/src/types/heading-tags';
import {
  BI_PRODUCT_OPTION_ACTION,
  BI_PRODUCT_OPTION_TYPE,
} from '@wix/wixstores-client-storefront-sdk/dist/es/src/constants';
import {AddToCartService} from '@wix/wixstores-client-storefront-sdk/dist/es/src/services/AddToCartService/AddToCartService';
import {ProductPriceBreakdown} from '@wix/wixstores-client-storefront-sdk/dist/es/src/services/ProductPriceBreakdown/ProductPriceBreakdown';
import {SiteStore} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/site-store/SiteStore';
import {ProductOptionType} from '@wix/wixstores-graphql-schema';
import type {ControllerParams} from '@wix/yoshi-flow-editor';
import {DangerousPublicDataOverrideFn} from '@wix/yoshi-flow-editor-runtime/build/cjs/helpers/createDangerousPublicDataOverride';
import _ from 'lodash';
import {
  AddToCartActionStatus,
  AddToCartFlowType,
  BI_APP_NAME,
  BiEventParam,
  Experiments,
  origin,
  ProductItemSlotIds,
  STORES_NAMESPACE,
} from '../constants';
import {ProductItemsService} from '../services/ProductItemsService';
import {ProductsVariantInfoMap} from '../services/ProductsOptionsService';
import {ProductsService} from '../services/ProductsService';
import {IStylesParamsValues} from '../styleParams/types';
import {
  AddToCartButtonPlacement,
  ButtonContentType,
  IGalleryControllerConfig,
  IPropsInjectedByViewerScript as IGalleryPropsInjectedByViewerScript,
  ImageModeId,
  ImageRatioId,
  IProduct,
  LoadMoreType,
  NamePriceLayout,
  PriceAndDiscountLayouts,
  ProductOptionsShowOptionsOption,
  ProductsManifest,
  type VeloInputs,
} from '../types/galleryTypes';
import {IPropsInjectedByViewerScript as ISliderGalleryPropsInjectedByViewerScript} from '../types/sliderGalleryTypes';
import {ReportError} from './GalleryController';
import {roundStyleParams} from './utils';

export abstract class BaseGalleryStore {
  protected config: IGalleryControllerConfig;
  protected readonly publicData: IControllerConfig['publicData'];
  protected readonly impressionId = generateGuid();
  protected addedToCartStatus: {[p: string]: AddToCartActionStatus} = {};
  protected productPriceBreakdown: ProductPriceBreakdown;
  protected productsManifest: ProductsManifest = {};
  protected productItemsService: ProductItemsService;
  protected productsService: ProductsService;
  protected addToCartService: AddToCartService;
  protected styles: IStylesParamsValues;
  protected allStyles: IStylesParamsValues[];
  protected appSettings: Record<string, any> = {};
  protected abstract get stylesParams();

  constructor(
    config: IControllerConfig,
    protected readonly dangerousPublicDataOverride: DangerousPublicDataOverrideFn,
    protected readonly siteStore: SiteStore,
    protected readonly updateComponent: (
      props: Partial<IGalleryPropsInjectedByViewerScript> | Partial<ISliderGalleryPropsInjectedByViewerScript>
    ) => void,
    protected readonly type: string,
    protected readonly compId: string,
    protected readonly flowAPI: ControllerParams['flowAPI'],
    protected readonly reportError: ReportError,
    protected readonly slotAPIFactory: ReturnType<typeof createSlotVeloAPIFactory>,
    protected veloInputs?: VeloInputs
  ) {
    this.config = config;
    this.publicData = _.cloneDeep(this.config.publicData);
    this.updateStyles();

    //todo: COMPONENT === null is not tested, be this check can be removed after bolt will stop sending nulls https://wix.slack.com/archives/CAKBA7TDH/p1555852184003900
    /* istanbul ignore next: hard to test */
    if (this.config.publicData.COMPONENT === null || this.config.publicData.COMPONENT === undefined) {
      this.config.publicData.COMPONENT = {};
    }
    this.productItemsService = new ProductItemsService(this.updateComponent);
  }

  protected updateStyles() {
    const {
      style: {styleParams},
      allStyles,
    } = this.config;
    roundStyleParams(styleParams);
    this.styles = getStylesValues(styleParams, this.stylesParams);
    this.allStyles = allStyles.map((styles) => getStylesValues(styles, this.stylesParams));
  }

  protected isTrueInAnyBreakpoint(predicate: (bpStyle: IStylesParamsValues) => boolean) {
    return this.allStyles.some((bpStyle) => predicate(bpStyle));
  }

  protected getCommonPropsToInject() {
    return {
      htmlTags: this.htmlTags,
      priceBreakdown: this.priceBreakdown,
      sendClickShippingInfoLinkSf: this.sendClickShippingInfoLinkSf.bind(this),
      imageMode: this.imageMode,
      imageRatio: this.imageRatio,
      isSEO: this.siteStore.seo.isInSEO(),
    };
  }

  protected abstract reportProductItemClick({productId, index}: {productId: string; index: number});

  protected sendClickShippingInfoLinkSf(productId: string) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.siteStore.webBiLogger.report(clickShippingInfoLinkSf({productId}));
  }

  protected getCommonPropsToUpdate() {
    const htmlTags = this.htmlTags;
    const productsVariantInfoMap = this.productsVariantInfoMap;
    const imageMode = this.imageMode;
    const imageRatio = this.imageRatio;

    return {htmlTags, productsVariantInfoMap, imageMode, imageRatio};
  }

  protected get priceBreakdown() {
    return {
      shouldRenderTaxDisclaimer: this.productPriceBreakdown.shouldShowTaxDisclaimer,
      shippingDisclaimer: this.productPriceBreakdown.shippingDisclaimer,
      taxDisclaimer: this.productPriceBreakdown.taxDisclaimer,
    };
  }

  protected get imageMode(): ImageModeId {
    return this.styles.gallery_imageMode;
  }

  protected get imageRatio(): ImageRatioId {
    return this.styles.galleryImageRatio;
  }

  protected get htmlTags() {
    return {
      productNameHtmlTag: this.publicData.COMPONENT.gallery_productNameHtmlTag
        ? this.publicData.COMPONENT.gallery_productNameHtmlTag
        : HeadingTags.P,
      headerTextHtmlTag: this.publicData.COMPONENT.gallery_headerTextHtmlTag || HeadingTags.H2,
      categoriesFiltersHtmlTag: this.publicData.COMPONENT.gallery_categoriesFiltersHtmlTag || HeadingTags.H2,
      categoryPageHeaderHtmlTag: this.publicData.COMPONENT.gallery_categoryPageHeaderHtmlTag || HeadingTags.H1,
    };
  }

  protected get biType() {
    switch (this.type) {
      case StoresWidgetID.SLIDER_GALLERY:
        return 'minigallery';
      case StoresWidgetID.CATEGORY_PAGE:
        return 'category';
      case StoresWidgetID.SEARCH_MODAL_GALLERY:
        return 'search suggestion box';
      case StoresWidgetID.SEARCH_RESULTS_GALLERY:
        return 'search results page';
      default:
        return 'gridgallery';
    }
  }

  protected async handleOptionSelectionsChange(params: {
    productId: string;
    selectionIds: number[];
    optionType: ProductOptionType;
  }) {
    if (this.getIsOptionsRevealEnabled()) {
      this.productsService.clearSelections();
    }

    await this.productsService.handleProductsOptionsChange({
      ...params,
      productsManifest: this.productsManifest,
    });

    const {product, variant} = this.productsService.getProductAndVariantById(params.productId);

    const isNewErrorStateViewerEnabled = this.siteStore.experiments.enabled(Experiments.GalleryNewErrorState);
    if (isNewErrorStateViewerEnabled) {
      const isAllOptionsSelected = product.options.length === params.selectionIds.length;
      if (isAllOptionsSelected) {
        this.updateAddToCartStatus(product.id, AddToCartActionStatus.IDLE);
      }
    }

    this.productsManifest[product.id].addToCartState = this.addToCartService.getButtonState({
      price: actualPrice(product),
      inStock: product.isInStock,
      isPreOrderState: isPreOrder(product, variant),
    });

    this.updateComponent({
      productsManifest: this.productsManifest,
      productsVariantInfoMap: this.productsVariantInfoMap,
      productsPriceRangeMap: this.productsService.productPriceRangeMap,
      products: this.productsService.products,
    });

    this.sendClickOnProductOptionBiEvent(params);
  }

  protected getIsOptionsRevealEnabled(): boolean {
    const productOptionsShowOptions = this.styles.gallery_productOptionsShowOptions;

    return (
      !this.siteStore.isMobile() &&
      productOptionsShowOptions === ProductOptionsShowOptionsOption.REVEAL &&
      this.siteStore.experiments.enabled(Experiments.GalleryProductOptionsVisibilitySettings)
    );
  }

  protected getStyleParamByDevice<T>(mobileStyleParam: T, defaultStyleParam: T, experimentEnabled = true): T {
    if (experimentEnabled && this.siteStore.isMobile() && mobileStyleParam !== undefined) {
      return mobileStyleParam;
    }
    return defaultStyleParam;
  }
  private sendClickOnProductOptionBiEvent(params: {productId: string; optionType: ProductOptionType}) {
    const {productId, optionType} = params;

    const optiontype = (
      {
        [ProductOptionType.DROP_DOWN]: BI_PRODUCT_OPTION_TYPE.LIST,
        [ProductOptionType.COLOR]: BI_PRODUCT_OPTION_TYPE.COLOR,
      } as const
    )[optionType];

    const {productType} = this.productsService.getProduct(productId);

    this.siteStore.webBiLogger.report(
      clickOnProductOptionSf({
        action: BI_PRODUCT_OPTION_ACTION.CHECKED,
        appName: BI_APP_NAME,
        optiontype,
        productId,
        productType,
        origin,
        viewMode: this.siteStore.biStorefrontViewMode,
      })
    );
  }

  protected get productsVariantInfoMap(): ProductsVariantInfoMap {
    const shouldShowVariantInfoMap = this.shouldShowProductOptions || this.isAddToCartFlowTypeIsQuickAddInAnyBreakpoint;
    return shouldShowVariantInfoMap ? this.productsService.getVariantInfoMap() : {};
  }

  protected get commonViewGallerySfParams() {
    const {gallery_loadMoreProductsType, gallery_showPrice, showQuickView, gallery_hoverType} = this.styles;

    const loadType = {
      [LoadMoreType.BUTTON]: BiEventParam.LoadMore,
      [LoadMoreType.PAGINATION]: BiEventParam.Pagination,
      [LoadMoreType.INFINITE]: BiEventParam.InfiniteScroll,
    }[gallery_loadMoreProductsType];

    return {
      type: this.type,
      component_id: this.compId,
      isMobileFriendly: this.siteStore.isMobileFriendly,
      addToCart: this.getStyleParamByDevice(
        this.styles['mobile:gallery_showAddToCartButton'],
        this.styles.gallery_showAddToCartButton
      ),
      navigationClick: this.biNavigationClick,
      hasPrice: gallery_showPrice,
      hasQuantity: this.getStyleParamByDevice(
        this.styles['mobile:gallery_showQuantity'],
        this.styles.gallery_showAddToCartQuantity
      ),
      priceBreakdown: this.productPriceBreakdown.priceBreakdownBIParam,
      hasQuickView: showQuickView,
      hoverType: gallery_hoverType.value,
      loadType,
      impressionId: this.impressionId,
    };
  }

  protected get commonViewGallerySfSettingsParams() {
    const {
      gallery_addToCartButtonPlacement,
      gallery_addToCartButtonContentType,
      gallery_priceAndDiscountLayout,
      gallery_namePriceLayout,
      gallery_showColorOptionsOnly,
      gallery_shouldLimitProductOptions,
      gallery_productOptionSwatchesLimit,
      gallery_quantityBoxWidth,
    } = this.styles;

    return {
      addToCartPlacement: _.startCase(AddToCartButtonPlacement[gallery_addToCartButtonPlacement]),
      priceAndDiscountLayout: _.startCase(_.camelCase(PriceAndDiscountLayouts[gallery_priceAndDiscountLayout])),
      nameAndPriceLayout: _.startCase(NamePriceLayout[gallery_namePriceLayout]),
      showColorOptionsOnly: gallery_showColorOptionsOnly,
      shouldLimitProductOptions: gallery_shouldLimitProductOptions,
      productOptionSwatchesLimit: gallery_productOptionSwatchesLimit,
      addToCartButtonContentType: _.startCase(ButtonContentType[gallery_addToCartButtonContentType]),
      quantityBoxWidth: gallery_quantityBoxWidth,
    };
  }

  private get biNavigationClick(): string {
    const {gallery_showAddToCartButton, gallery_addToCartAction} = this.styles;
    if (!gallery_showAddToCartButton) {
      return '';
    } else if (gallery_addToCartAction === AddToCartActionOption.MINI_CART) {
      return 'mini-cart';
    } else if (gallery_addToCartAction === AddToCartActionOption.CART) {
      return 'cart';
    }
    return 'none';
  }

  protected abstract get shouldShowProductOptions(): boolean;

  protected async setProductItemSlotsProps(products: IProduct[]) {
    const gallerySlotIds = Object.values(ProductItemSlotIds);

    /* istanbul ignore next: yoshi missing a testkit for repeated slots  */
    if (this.siteStore.experiments.enabled(Experiments.RepeatedSlots)) {
      try {
        for (const product of products) {
          const slot = await this.slotAPIFactory.createRepeatedSlotAPI(
            ProductItemSlotIds.GalleryItemAdditionalButtons,
            product.id
          );
          slot.productId = product.id;
          slot.sku = product.sku;
        }
      } catch (e) {
        this.reportError(e);
      }
    }

    for (const slotId of gallerySlotIds) {
      try {
        const slot = this.slotAPIFactory.getSlotAPI(slotId);
        slot.namespace = STORES_NAMESPACE;

        slot.resourceList = products.map((product) => {
          const maybeAdditionalButtonsSlotFields =
            slotId === ProductItemSlotIds.GalleryItemAdditionalButtons
              ? {
                  sku: product.sku,
                  isPreOrder: !!isPreOrder(product),
                }
              : {};

          return {
            id: product.id,
            ...maybeAdditionalButtonsSlotFields,
          };
        });
      } catch (e) {
        /* istanbul ignore next: nothing to test  */
        this.reportError(e);
      }
    }
  }

  protected get isUsingAlternativeCurrency() {
    return this.siteStore.currency !== this.siteStore.getCurrentCurrency();
  }

  protected readonly updateAddToCartStatus = (productId: string, status: AddToCartActionStatus) => {
    this.addedToCartStatus[productId] = status;
    this.updateComponent({addedToCartStatus: this.addedToCartStatus});
  };

  protected get isAddToCartFlowTypeIsQuickAddInAnyBreakpoint(): boolean {
    const addToCartFlowTypeStyleParamKey = this.stylesParams.gallery_addToCartFlowType.key;
    return this.isTrueInAnyBreakpoint(
      (bpStyle) => bpStyle[addToCartFlowTypeStyleParamKey] === AddToCartFlowType.QUICK_ADD
    );
  }
}
